import type {
  DashboardCoachStudentCount,
  DashboardCoachWorkload,
  DashboardMetrics,
  DashboardNewStudentPending,
  DashboardOperationalSnapshot,
  DashboardWorkoutStatusSlice,
} from '../types/dashboard';

const EMPTY_METRICS: DashboardMetrics = {
  totalStudents: 0,
  workoutsPending: 0,
  workoutsDelivered: 0,
  workoutsOverdue: 0,
  deliveriesToday: 0,
  deliveriesTomorrow: 0,
  paymentsOverdue: 0,
  newStudentsWithPendingWorkout: 0,
};

const toNumber = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const mapMetrics = (raw: Record<string, unknown> | null | undefined): DashboardMetrics => ({
  totalStudents: toNumber(raw?.totalStudents),
  workoutsPending: toNumber(raw?.workoutsPending),
  workoutsDelivered: toNumber(raw?.workoutsDelivered),
  workoutsOverdue: toNumber(raw?.workoutsOverdue),
  deliveriesToday: toNumber(raw?.deliveriesToday),
  deliveriesTomorrow: toNumber(raw?.deliveriesTomorrow),
  paymentsOverdue: toNumber(raw?.paymentsOverdue),
  newStudentsWithPendingWorkout: toNumber(raw?.newStudentsWithPendingWorkout),
});

const mapWorkoutStatus = (raw: unknown): DashboardWorkoutStatusSlice[] => {
  if (!Array.isArray(raw)) {
    return [];
  }

  return raw
    .map((item) => {
      if (!item || typeof item !== 'object') {
        return null;
      }

      const row = item as Record<string, unknown>;
      const name = typeof row.name === 'string' ? row.name : '';
      if (!name) {
        return null;
      }

      return {
        name,
        value: toNumber(row.value),
      };
    })
    .filter((item): item is DashboardWorkoutStatusSlice => item != null);
};

const mapStudentsByCoach = (raw: unknown): DashboardCoachStudentCount[] => {
  if (!Array.isArray(raw)) {
    return [];
  }

  return raw
    .map((item) => {
      if (!item || typeof item !== 'object') {
        return null;
      }

      const row = item as Record<string, unknown>;
      const coachName = typeof row.coachName === 'string' ? row.coachName : '';
      if (!coachName) {
        return null;
      }

      return {
        coachId: toNumber(row.coachId),
        coachName,
        total: toNumber(row.total),
      };
    })
    .filter((item): item is DashboardCoachStudentCount => item != null);
};

const mapByCoach = (raw: unknown): DashboardCoachWorkload[] => {
  if (!Array.isArray(raw)) {
    return [];
  }

  return raw
    .map((item) => {
      if (!item || typeof item !== 'object') {
        return null;
      }

      const row = item as Record<string, unknown>;
      const coachName = typeof row.coachName === 'string' ? row.coachName : '';
      if (!coachName) {
        return null;
      }

      return {
        coachId: toNumber(row.coachId),
        coachName,
        total: toNumber(row.total),
        pending: toNumber(row.pending),
        completed: toNumber(row.completed),
        overdue: toNumber(row.overdue),
        today: toNumber(row.today),
        tomorrow: toNumber(row.tomorrow),
      };
    })
    .filter((item): item is DashboardCoachWorkload => item != null);
};

const mapNewStudentsPending = (raw: unknown): DashboardNewStudentPending[] => {
  if (!Array.isArray(raw)) {
    return [];
  }

  return raw
    .map((item) => {
      if (!item || typeof item !== 'object') {
        return null;
      }

      const row = item as Record<string, unknown>;
      const studentName = typeof row.studentName === 'string' ? row.studentName : '';
      if (!studentName) {
        return null;
      }

      return {
        studentId: toNumber(row.studentId),
        studentName,
        coachName: typeof row.coachName === 'string' ? row.coachName : 'Sem professora',
      };
    })
    .filter((item): item is DashboardNewStudentPending => item != null);
};

export function mapDashboardOperationalSnapshot(raw: unknown): DashboardOperationalSnapshot {
  const payload = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const metricsRaw = payload.metrics && typeof payload.metrics === 'object'
    ? payload.metrics as Record<string, unknown>
    : null;

  return {
    generatedAt: typeof payload.generatedAt === 'string' ? payload.generatedAt : new Date().toISOString(),
    competence: typeof payload.competence === 'string' ? payload.competence : '',
    metrics: metricsRaw ? mapMetrics(metricsRaw) : EMPTY_METRICS,
    workoutStatus: mapWorkoutStatus(payload.workoutStatus),
    studentsByCoach: mapStudentsByCoach(payload.studentsByCoach),
    byCoach: mapByCoach(payload.byCoach),
    newStudentsPending: mapNewStudentsPending(payload.newStudentsPending),
  };
}
